/**
 * @file SearchAutocomplete component
 *
 * Provides autocomplete suggestions as the user types into a search input.
 * Triggers after 3 characters, debounces subsequent keystrokes.
 *
 * Usage: add [data-lta-search-autocomplete] to the wrapper element.
 * The endpoint is read from the data-endpoint attribute on the same element.
 *
 * Fires a custom "autocomplete:select" event on the wrapper when a result is chosen,
 * with { id, label, link, summary } in event.detail.
 */

import { debounce } from 'lodash-es';
import Request from '../../utils/request';
import BaseComponent from '../BaseComponent';
import EventHandler from '../../utils/dom/event-handler';
import Data from '../../utils/dom/data';

const NAME = 'SearchAutocomplete';
const DATA_KEY = 'lta.searchautocomplete';
const EVENT_KEY = `.${DATA_KEY}`;
const DATA_API_KEY = '.data-api';
const EVENT_CLICK_DATA_API = `click${EVENT_KEY}${DATA_API_KEY}`;
const SELECTOR_DATA_TOGGLE = '[data-lta-search-autocomplete]';
const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;
const EVENT_SELECT = 'autocomplete:select';
let instanceCount = 0;

class SearchAutocomplete extends BaseComponent {
	constructor(element) {
		super(element);
		instanceCount += 1;
		this._instanceId = `lta-ac-${instanceCount}`;
		this._fetchInProgress = false;
		this._activeIndex = -1;
		this._results = [];
		this._init();
	}

	static get NAME() {
		return NAME;
	}

	static get DATA_KEY() {
		return DATA_KEY;
	}

	dispose() {
		super.dispose();
	}

	// ─── Private ────────────────────────────────────────────────────────────────

	_init() {
		this._input = this._element.querySelector('.js-search-autocomplete-input');
		this._dropdown = this._element.querySelector('.lta-search-autocomplete__dropdown');
		this._list = this._element.querySelector('.lta-search-autocomplete__results');
		this._loading = this._element.querySelector('.lta-search-autocomplete__loading');
		this._noResults = this._element.querySelector('.lta-search-autocomplete__no-results');

		if (!this._input || !this._dropdown) return;

		this._endpoint = this._element.dataset.endpoint || '/api/search/autocomplete';

		this._debouncedFetch = debounce(this._fetchResults.bind(this), DEBOUNCE_MS);

		EventHandler.on(this._input, 'input', this._onInput.bind(this));
		EventHandler.on(this._input, 'keydown', this._onKeydown.bind(this));
		EventHandler.on(this._input, 'focus', this._onFocus.bind(this));
		EventHandler.on(document, 'click', this._onDocumentClick.bind(this));
	}

	_onInput() {
		const query = this._input.value.trim();
		this._activeIndex = -1;

		if (query.length < MIN_CHARS) {
			this._closeDropdown();
			return;
		}

		this._debouncedFetch(query);
	}

	_onFocus() {
		const query = this._input.value.trim();
		if (query.length >= MIN_CHARS && this._results.length > 0) {
			this._openDropdown();
		}
	}

	_onKeydown(event) {
		if (!this._isDropdownOpen()) return;

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				this._moveFocus(1);
				break;
			case 'ArrowUp':
				event.preventDefault();
				this._moveFocus(-1);
				break;
			case 'Enter':
				event.preventDefault();
				this._selectActive();
				break;
			case 'Escape':
				this._closeDropdown();
				this._input.focus();
				break;
		}
	}

	_onDocumentClick(event) {
		if (!this._element.contains(event.target)) {
			this._closeDropdown();
		}
	}

	async _fetchResults(query) {
		if (this._fetchInProgress) return;
		this._fetchInProgress = true;

		this._showLoading(true);

		try {
			const data = await Request.get(this._endpoint, { q: query });
			this._results = (data && data.items) || [];
			this._renderResults(this._results);
		} catch {
			this._results = [];
			this._renderResults([]);
		} finally {
			this._fetchInProgress = false;
			this._showLoading(false);
		}
	}

	_renderResults(items) {
		this._list.innerHTML = '';
		this._activeIndex = -1;

		if (items.length === 0) {
			this._noResults.hidden = false;
			this._list.hidden = true;
			this._openDropdown();
			return;
		}

		this._noResults.hidden = true;
		this._list.hidden = false;

		for (const [index, item] of items.entries()) {
			this._list.append(this._createResultItem(item, index));
		}

		this._openDropdown();
	}

	_createResultItem(item, index) {
		const li = document.createElement('li');
		li.className = 'lta-search-autocomplete__item';
		li.setAttribute('role', 'option');
		li.setAttribute('id', `${this._instanceId}-item-${index}`);
		li.dataset.index = index;

		const link = document.createElement('a');
		link.href = item.link;
		link.className = 'lta-search-autocomplete__link';

		const label = document.createElement('span');
		label.className = 'lta-search-autocomplete__label';
		label.textContent = item.label;

		const summary = document.createElement('span');
		summary.className = 'lta-search-autocomplete__summary';
		summary.textContent = item.summary;

		link.append(label, summary);
		li.append(link);

		EventHandler.on(li, 'click', (event) => {
			event.preventDefault();
			this._selectItem(item);
		});

		return li;
	}

	_moveFocus(direction) {
		const items = this._list.querySelectorAll('.lta-search-autocomplete__item');
		if (items.length === 0) return;

		const newIndex = Math.max(-1, Math.min(this._activeIndex + direction, items.length - 1));
		this._setActiveItem(newIndex, items);
	}

	_setActiveItem(index, items) {
		if (this._activeIndex >= 0 && items[this._activeIndex]) {
			items[this._activeIndex].classList.remove('is-active');
			items[this._activeIndex].setAttribute('aria-selected', 'false');
		}

		this._activeIndex = index;

		if (index >= 0 && items[index]) {
			items[index].classList.add('is-active');
			items[index].setAttribute('aria-selected', 'true');
			this._input.setAttribute('aria-activedescendant', `${this._instanceId}-item-${index}`);
		} else {
			this._input.removeAttribute('aria-activedescendant');
		}
	}

	_selectActive() {
		if (this._activeIndex >= 0 && this._results[this._activeIndex]) {
			this._selectItem(this._results[this._activeIndex]);
		}
	}

	_selectItem(item) {
		this._input.value = item.label;
		this._closeDropdown();

		const event = new CustomEvent(EVENT_SELECT, {
			bubbles: true,
			detail: { id: item.id, label: item.label, link: item.link, summary: item.summary }
		});
		this._element.dispatchEvent(event);

		// Navigate to the selected result's link
		window.location.href = item.link;
	}

	_openDropdown() {
		this._dropdown.hidden = false;
		this._dropdown.classList.add('lta-search-autocomplete__dropdown--open');
		this._input.setAttribute('aria-expanded', 'true');
	}

	_closeDropdown() {
		this._dropdown.hidden = true;
		this._dropdown.classList.remove('lta-search-autocomplete__dropdown--open');
		this._input.setAttribute('aria-expanded', 'false');
		this._input.removeAttribute('aria-activedescendant');
		this._activeIndex = -1;
	}

	_isDropdownOpen() {
		return !this._dropdown.hidden;
	}

	_showLoading(show) {
		this._loading.hidden = !show;
	}
}

/**
 * Data API auto-initialisation
 */
EventHandler.on(document, EVENT_CLICK_DATA_API, SELECTOR_DATA_TOGGLE, function () {
	Data.getData(this, DATA_KEY) || new SearchAutocomplete(this);
});

export default SearchAutocomplete;
